import { colors, LineChartOption } from '@creditsights/styleguide-web';
import { round } from 'lodash';
import isEmpty from 'lodash/isEmpty';
import { MarkLineComponentOption } from 'echarts/components';
import { COMPANY_MODEL, CreMetric, HistoricalRecord, RiskProductsData } from '../../../../models/RiskProducts';
import { fontConfig, getTimeSeriesBaseConfig } from '../../../../utils/chartUtils';
import { DATE_FORMATS, formatCommonDate, formatDate } from '../../../../utils/dateUtils';
import {
  filterRiskMetricsByYearRange,
  mapRiskMetricsToYearsAxisValues,
  sortMetricsDescByCalculationDate,
} from '../RiskProductsWidget.logic';
import {
  EUROPE_REGION,
  FINANCIAL_EURO_CRL,
  FINANCIAL_NON_EURO_CRL,
  NON_FINANCIAL_CRL,
} from './CreChart.logic.constants';
import { CreditRiskLevelEstimate } from './CreChart.types';
import { TimeFrameOptions } from '../RiskProductsWidget.types';

export const hasCreMetrics = (creMetrics: CreMetric[]): boolean => {
  const creValues = creMetrics.map((metric) => metric.crePercentage).filter((cre) => cre !== null);
  return !(isEmpty(creMetrics) || isEmpty(creValues));
};

// ------------ Filter cre metrics ------------
export const getCreMetrics = (riskMetrics: RiskProductsData): CreMetric[] => {
  const creMetrics = riskMetrics?.data.length > 0 ? processCreMetrics(riskMetrics.data) : [];
  return sortMetricsDescByCalculationDate(creMetrics);
};

export const processCreMetrics = (riskMetrics: HistoricalRecord[]): CreMetric[] => {
  const filteredRiskMetrics = filterRiskMetricsByYearRange(riskMetrics);
  return getValidCreMetrics(filteredRiskMetrics);
};

const getValidCreMetrics = (allCreMetrics: HistoricalRecord[]): CreMetric[] => {
  const expectedCreMetrics: CreMetric[] = [];
  allCreMetrics.forEach((metric) => {
    const creValue = metric.comulative_default_probalitity_1y;
    if (creValue) {
      const crePercentage = creValue * 100;
      const creValid = crePercentage <= 100 && crePercentage > 0;
      if (creValid) {
        const precision = getPrecision(crePercentage);
        expectedCreMetrics.push({
          crePercentage: round(crePercentage, precision),
          creditRiskLevel: metric.credit_risk_level || '',
          calculationDate: metric.calculation_date,
        });
      }
    }
  });
  return expectedCreMetrics;
};

const getPrecision = (value: number) => {
  if (value < 0.01) {
    return 5;
  }

  if (value >= 0.01 && value < 0.1) {
    return 3;
  }

  return 2;
};

const getYAxisTicksByCompany = (companyModelId: string, companyRegionId: string): CreditRiskLevelEstimate[] => {
  if (companyModelId === COMPANY_MODEL.BANKS && companyRegionId === EUROPE_REGION) {
    return FINANCIAL_EURO_CRL;
  }

  switch (companyModelId) {
    case COMPANY_MODEL.AEROSPACE:
    case COMPANY_MODEL.AIRLINES:
    case COMPANY_MODEL.AUTOMOTIVE:
    case COMPANY_MODEL.CAPITAL_GOODS:
    case COMPANY_MODEL.CHEMICALS:
    case COMPANY_MODEL.CONSTRUCTION_AGGREGATES:
    case COMPANY_MODEL.CONSUMER:
    case COMPANY_MODEL.DIVERSIFIED_CONGLOMERATES:
    case COMPANY_MODEL.FOOD_BEVERAGES:
    case COMPANY_MODEL.GAMING_LEISURE:
    case COMPANY_MODEL.HEALTHCARE_PHARMACEUTICALS:
    case COMPANY_MODEL.HOMEBUILDING:
    case COMPANY_MODEL.MEDIA_CABLE:
    case COMPANY_MODEL.METAL_MINING:
    case COMPANY_MODEL.OIL_AND_GAS:
    case COMPANY_MODEL.PAPER_PACKAGING:
    case COMPANY_MODEL.PIPELINES_MLP:
    case COMPANY_MODEL.RETAIL_GROCERS:
    case COMPANY_MODEL.SHIPPING_INFRASTRUCTURE:
    case COMPANY_MODEL.TECHNOLOGY:
    case COMPANY_MODEL.TELECOMM:
    case COMPANY_MODEL.TRUCKING_RAILS:
    case COMPANY_MODEL.UTILITIES:
    case COMPANY_MODEL.TMT:
      return NON_FINANCIAL_CRL;
    case COMPANY_MODEL.FINANCE:
    case COMPANY_MODEL.BANKS:
    case COMPANY_MODEL.INSURANCE:
    case COMPANY_MODEL.LHINSURANCE:
    case COMPANY_MODEL.PCINSURANCE:
    case COMPANY_MODEL.REITS:
      return FINANCIAL_NON_EURO_CRL;
    default:
      return [];
  }
};

const isALog10Value = (value: number) => Number.isInteger(Math.log10(value));

const formatDecimalNumber = (value: number) =>
  value.toFixed(Math.max(2, (value.toString().split('.')[1] || []).length));

export const getMarkLineData = (
  modelId: string,
  regionId: string,
  maxValue: number
): MarkLineComponentOption['data'] => {
  const crlValues: CreditRiskLevelEstimate[] = getYAxisTicksByCompany(modelId, regionId);
  const markLines: MarkLineComponentOption['data'] = [];

  crlValues.forEach((crlValue) => {
    if (crlValue.value !== maxValue) {
      markLines.push({
        name: crlValue.level,
        yAxis: crlValue.value,
        symbol: 'none',
        label: {
          ...fontConfig,
          fontStyle: 'normal',
          position: 'insideStartTop',
          formatter: crlValue.level,
          color: colors.tertiary700,
        },
      });

      if (!isALog10Value(crlValue.value)) {
        markLines.push({
          name: crlValue.level,
          yAxis: crlValue.value,
          symbol: 'none',
          label: {
            ...fontConfig,
            fontStyle: 'normal',
            position: 'end',
            formatter: formatDecimalNumber(crlValue.value),
            color: colors.tertiary1000,
            distance: 45,
          },
        });
      }
    }
  });

  return markLines;
};

const getYAxisRanges = (yAxisData: number[]): number[] => {
  const minLogValue = Math.floor(Math.log10(Math.min(...yAxisData, 1)));
  const maxLogValue = Math.ceil(Math.log10(Math.max(...yAxisData, 1)));
  const minValue = Number((10 ** minLogValue).toFixed(Math.abs(Number.isFinite(minLogValue) ? minLogValue : 0)));
  const maxValue = Number((10 ** maxLogValue).toFixed(Math.abs(maxLogValue)));
  return [minValue, maxValue];
};

const getTooltipHtml = (params, creMetrics): string => {
  return `
   <span>
     ${formatCommonDate(creMetrics[params[0].dataIndex].calculationDate)}
   </span>
   <span>
     ${params[0].value}%
   </span>
   <span style="position: absolute; bottom: 0; left: 50%; width: 0; height: 0; margin: 0 0 -4px -4px; border-style: solid; border-width: 4px 4px 0 4px; border-color: #141d24 transparent transparent transparent;"></span>
`;
};

export const getCreChartConfig = (
  creMetrics: CreMetric[],
  modelId: string,
  regionId: string,
  timeframe?: TimeFrameOptions
): LineChartOption => {
  const yAxisData = creMetrics.map((metric) => metric.crePercentage);
  const xAxisValues = mapRiskMetricsToYearsAxisValues(creMetrics);

  const yAxisRanges = getYAxisRanges(yAxisData);
  const minValue = yAxisRanges[0];
  const maxValue = yAxisRanges[1];

  const baseChartOptionConfig: LineChartOption = getTimeSeriesBaseConfig(xAxisValues.length, timeframe);

  return {
    ...baseChartOptionConfig,
    legend: {
      ...baseChartOptionConfig.legend,
    },
    tooltip: {
      ...baseChartOptionConfig.tooltip,
      formatter: (params) => getTooltipHtml(params, creMetrics),
    },
    xAxis: [
      {
        ...baseChartOptionConfig.xAxis?.[0],
        data: xAxisValues,
        axisLabel: {
          ...baseChartOptionConfig.xAxis?.[0].axisLabel,
          formatter: (_, index) => {
            return formatDate(creMetrics[index].calculationDate, DATE_FORMATS.MONTH_YEAR_MLA);
          },
        },
        inverse: true,
      },
    ],
    yAxis: [
      {
        type: 'log',
        min: minValue,
        max: maxValue,
        axisLabel: {
          ...fontConfig,
          color: colors.tertiary1000,
          fontStyle: 'normal',
          formatter: formatDecimalNumber,
        },
      },
    ],
    series: [
      {
        name: 'CRE',
        type: 'line',
        step: 'start',
        showSymbol: false,
        symbol: 'none',
        data: yAxisData,
        color: '#6D5989',
        smooth: true,
        lineStyle: {
          join: 'miter',
        },
        silent: true,
        markLine: {
          silent: true,
          lineStyle: { type: 'solid', color: colors.tertiary300 },
          data: getMarkLineData(modelId, regionId, maxValue),
          symbol: ['none', 'none'],
          animation: false,
        },
      },
    ],
  };
};
